import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { listarPecas } from '../../../api/previsaoPecas/previsaoPecasApi';
import { FiltroPecas, ItemPeca, ListaDePecas, ListarPecasParams } from '../../../interfaces/PrevisaoPecas';
import Paginacao from '../../../components/paginacao/Paginacao';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { notify } from '../../../shared/feedback/notify';
import { textoDaChegada } from './BarraDeChegada';
import { CELULA_DE_UMA_LINHA, STATUS_DA_PREVISAO, formatarDiaPuro, rotuloDoStatus } from '../formatos';

const POR_PAGINA = 20;

const SUB_ABAS: { valor: FiltroPecas; rotulo: string }[] = [
  { valor: '', rotulo: 'Todas as peças' },
  { valor: 'oficina', rotulo: 'Oficina' },
  { valor: 'materiais', rotulo: 'Materiais' },
  { valor: 'oficina_externa', rotulo: 'Oficina Externa' },
  { valor: 'chegam_hoje', rotulo: 'Chegam hoje' },
  { valor: 'atrasados', rotulo: 'Atrasados' },
  { valor: 'recebidos', rotulo: 'Recebidos' },
];

const destino = (i: ItemPeca, aba: FiltroPecas) =>
  aba === 'oficina_externa' ? (i.pedido.oficina ?? 'não informada') : (i.pedido.placa ?? i.pedido.oficina ?? 'não informado');

interface Props {
  filtros: Omit<ListarPecasParams, 'filtro' | 'pagina' | 'limite'>;
  versao: number;
  onAbrirItem: (item: ItemPeca) => void;
  estiloDoCard: (comPaginacao: boolean) => React.CSSProperties;
  /** A sub-aba atual sobe para a página: o filtro de recebimento só existe em "Recebidos". */
  onTrocarSubAba?: (aba: FiltroPecas) => void;
}

/**
 * A aba Peças: uma peça de pedido por linha — a antiga aba Itens, agora com a chegada por
 * quantidade (o "recebido" binário deixou de existir: a peça está recebida quando o saldo zera).
 *
 * Todos os cartões aparecem para quem vê a tela (decisão do Victor, 24/10/2026 — antes "Itens em
 * trânsito" e "Atrasados" eram só do ADMIN).
 */
const AbaPecas: React.FC<Props> = ({ filtros, versao, onAbrirItem, estiloDoCard, onTrocarSubAba }) => {
  const [aba, setAba] = useState<FiltroPecas>('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<ListaDePecas | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => setPagina(1), [filtros]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(
        await listarPecas({
          ...filtros,
          filtro: aba || undefined,
          // O período de recebimento só existe na sub-aba Recebidos: nas outras, a peça não chegou.
          recebidoDe: aba === 'recebidos' ? filtros.recebidoDe : undefined,
          recebidoAte: aba === 'recebidos' ? filtros.recebidoAte : undefined,
          pagina,
          limite: POR_PAGINA,
        }),
      );
      setFalhou(false);
    } catch (error) {
      setDados(null);
      setFalhou(true);
      notify.errorFrom(error, 'Não foi possível carregar as peças. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setCarregando(false);
    }
  }, [filtros, aba, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  const trocarAba = (valor: FiltroPecas) => {
    setAba(valor);
    setPagina(1);
    onTrocarSubAba?.(valor);
  };

  const resumo = dados?.resumo;
  const cartoes: { rotulo: string; valor: number | undefined; aba: FiltroPecas | null; apoio?: string; titulo?: string }[] = [
    { rotulo: 'Peças em trânsito', valor: resumo?.itensEmTransito, aba: null },
    { rotulo: 'Atrasadas', valor: resumo?.atrasados, aba: 'atrasados' },
    { rotulo: 'Chegam hoje', valor: resumo?.chegamHoje, aba: 'chegam_hoje' },
    {
      rotulo: 'Recebidas na semana',
      valor: resumo?.recebidosNaSemana,
      aba: 'recebidos_semana',
      apoio: 'Segunda a domingo',
      titulo: resumo?.recebidosPorSemana?.length
        ? `Últimas semanas:\n${resumo.recebidosPorSemana
            .map((s) => `${formatarDiaPuro(s.inicio)} a ${formatarDiaPuro(s.fim)}: ${s.total}`)
            .join('\n')}`
        : undefined,
    },
  ];

  const colunas: ColunaTabela<ItemPeca>[] = useMemo(
    () => [
      {
        titulo: 'Peça',
        largura: 230,
        larguraMaxima: 280,
        texto: (i) => `${i.produtoDescricao ?? 'Peça sem descrição'}${i.produtoCodigo ? ` (${i.produtoCodigo})` : ''}`,
        render: (i) => (
          <button type="button" className="rastreamento-placa-botao" onClick={() => onAbrirItem(i)}>
            {i.produtoDescricao ?? 'Peça sem descrição'}
          </button>
        ),
      },
      {
        titulo: aba === 'materiais' ? 'Grupo' : aba === 'oficina_externa' ? 'Oficina' : 'Veículo / destino',
        largura: 120,
        larguraMaxima: 170,
        texto: (i) => destino(i, aba),
        render: (i) => <span style={CELULA_DE_UMA_LINHA}>{destino(i, aba)}</span>,
      },
      {
        titulo: 'Pedido - Fornecedor',
        largura: 220,
        larguraMaxima: 260,
        texto: (i) => `${i.pedido.numero ?? 'sem número'} - ${i.pedido.fornecedorNome ?? 'não identificado'}`,
        render: (i) => (
          <span style={CELULA_DE_UMA_LINHA}>
            {i.pedido.numero ?? 'sem número'} - {i.pedido.fornecedorNome ?? 'não identificado'}
          </span>
        ),
      },
      {
        titulo: 'Chegada',
        largura: 170,
        larguraMaxima: 220,
        texto: (i) => textoDaChegada(i.chegada, i.pedida, i.unidade ?? 'un'),
        render: (i) => <span style={CELULA_DE_UMA_LINHA}>{textoDaChegada(i.chegada, i.pedida, i.unidade ?? 'un')}</span>,
      },
      {
        titulo: 'Previsão',
        largura: 100,
        larguraMaxima: 100,
        texto: (i) => formatarDiaPuro(i.previsao),
      },
      {
        titulo: 'Status',
        largura: 170,
        larguraMaxima: 190,
        texto: (i) => rotuloDoStatus(i.status),
        render: (i) => (
          <span className={`cilia-badge ${STATUS_DA_PREVISAO[i.status].classe}`}>
            {rotuloDoStatus(i.status)}
          </span>
        ),
      },
    ],
    [aba, onAbrirItem],
  );

  const total = dados?.total;
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / POR_PAGINA)) : 1;
  const temPaginacao = totalPaginas > 1;

  return (
    <>
      {!falhou && (
        <div className="do-kpis" style={{ width: '100%' }}>
          {cartoes.map((c) => {
            const conteudo = (
              <>
                <div className="do-kpi-rotulo">{c.rotulo}</div>
                <div className="do-kpi-valor">{c.valor === undefined ? '...' : c.valor}</div>
                {c.apoio && <div className="do-kpi-apoio">{c.apoio}</div>}
              </>
            );
            return c.aba === null ? (
              <div key={c.rotulo} className="do-kpi" title={c.titulo}>
                {conteudo}
              </div>
            ) : (
              <button
                key={c.rotulo}
                type="button"
                className="do-kpi do-kpi-clicavel"
                title={c.titulo ?? `Ver as peças: ${c.rotulo}`}
                onClick={() => trocarAba(c.aba as FiltroPecas)}
                style={aba === c.aba ? { borderColor: '#ea5d0e' } : undefined}
              >
                {conteudo}
              </button>
            );
          })}
        </div>
      )}

      <div className="sub-abas">
        {SUB_ABAS.map((a) => (
          <button
            key={a.valor || 'todas'}
            type="button"
            className={`sub-aba ${aba === a.valor ? 'active' : ''}`}
            onClick={() => trocarAba(a.valor)}
          >
            {a.rotulo}
            {aba === a.valor && !falhou && total !== undefined ? ` (${total})` : ''}
          </button>
        ))}
      </div>

      {falhou && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar as peças. A ausência de peças aqui não significa que não há peça a caminho.
          Recarregue a página; se persistir, acione a TI.
        </div>
      )}

      {!falhou && (
        <div style={estiloDoCard(temPaginacao)}>
          <TabelaSistema
            colunas={colunas}
            itens={dados?.itens ?? []}
            chave={(i) => i.id}
            vazio="Nenhuma peça encontrada."
            carregando={carregando}
          />
          {temPaginacao && <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />}
        </div>
      )}
    </>
  );
};

export default AbaPecas;
